import React, { useState, useEffect, useRef } from 'react';
import { API, fetchSites as apiFetchSites, fetchUsers as apiFetchUsers } from '../utils/api';
import axios from 'axios';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';

const AddTaskModal = ({ isOpen, onClose, onTaskCreated }) => {
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    site_id: '',
    assigned_to: [],
    status: 'pending',
    priority: 'medium',
    start_date: '',
    due_date: '',
    task_capacity: 1.0
  });

  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const fileInputRef = useRef(null);

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'danger'
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchSitesAndUsers();
      document.body.style.overflow = 'hidden';
      // Reset modal states when opening to prevent stale state
      setSuccessModal({ isOpen: false, title: '', message: '' });
      setErrorModal({ isOpen: false, title: '', message: '' });
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchSitesAndUsers = async () => {
    try {
      const [sitesData, usersData] = await Promise.all([
        apiFetchSites(),
        apiFetchUsers()
      ]);
      setSites(sitesData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to fetch sites and users:', error);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'text/plain', 'text/csv', 'application/json', 'text/markdown'
    ];
    
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid File Type',
        message: `File type not allowed: ${file.name}. Please select PDF, images, or text files.`
      });
      return false;
    }
    
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrorModal({
        isOpen: true,
        title: 'File Too Large',
        message: `File too large: ${file.name}. Maximum size is 10MB.`
      });
      return false;
    }
    
    return true;
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (selectedFiles.length + files.length > 5) {
      setErrorModal({
        isOpen: true,
        title: 'Too Many Files',
        message: 'Maximum 5 files allowed per task.'
      });
      return;
    }
    
    const validFiles = files.filter(validateFile);
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadAttachments = async (taskId) => {
    const token = localStorage.getItem('token');
    const errors = [];
    
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(
          `${API}/admin/tasks/${taskId}/attachments`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        if (response.status !== 200) {
          errors.push(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        // Capture specific error message (e.g., storage limit exceeded)
        const errorMessage = error.response?.data?.detail || `Failed to upload ${file.name}`;
        errors.push(errorMessage);
      }
    }
    
    if (errors.length > 0) {
      const error = new Error(errors[0]); // Use first error as primary
      error.allErrors = errors;
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Client-side validation: if both dates are present, start must be ≤ due.
      if (newTask.start_date && newTask.due_date && newTask.start_date > newTask.due_date) {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert('Start date cannot be after the due date.');
        }
        setIsSubmitting(false);
        return;
      }

      const taskData = {
        ...newTask,
        ...(newTask.due_date ? { due_date: newTask.due_date } : {}),
        ...(newTask.start_date ? { start_date: newTask.start_date } : {})
      };
      if (!taskData.due_date) {
        delete taskData.due_date;
      }
      if (!taskData.start_date) {
        delete taskData.start_date;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/tasks`, taskData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const createdTask = response.data;
      
      let uploadFailed = false;
      let uploadErrorMessage = '';
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          await uploadAttachments(createdTask.id);
        } catch (uploadError) {
          console.error('Failed to upload some attachments:', uploadError);
          uploadFailed = true;
          uploadErrorMessage = uploadError.message || 'Some file uploads failed.';
        } finally {
          setUploadingFiles(false);
        }
      }
      
      setNewTask({
        title: '',
        description: '',
        site_id: '',
        assigned_to: [],
        status: 'pending',
        priority: 'medium',
        start_date: '',
        due_date: '',
        task_capacity: 1.0
      });
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onTaskCreated && onTaskCreated(createdTask);
      
      if (uploadFailed) {
        onClose();
        setErrorModal({
          isOpen: true,
          title: 'Partial Success',
          message: `Task created successfully, but file upload failed: ${uploadErrorMessage}`
        });
      } else {
        setSuccessModal({
          isOpen: true,
          title: 'Task Created',
          message: `Task "${createdTask.title}" has been successfully created.`
        });
        // Close the create modal after a brief delay to allow success modal to show
        setTimeout(() => {
          onClose();
        }, 100);
      }
    } catch (error) {
      console.error('Task creation error:', error.response?.data);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setErrorModal({
        isOpen: true,
        title: 'Creation Failed',
        message: 'Failed to create task: ' + errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !uploadingFiles) {
      onClose();
    }
  };

  // Backdrop click disabled - modal must be closed via buttons

  const toggleUserAssignment = (userId) => {
    setNewTask(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  // Get users for selected site
  const getSiteUsers = () => {
    if (!newTask.site_id) return users;
    const selectedSite = sites.find(s => s.id === newTask.site_id);
    if (!selectedSite || !selectedSite.assigned_users) return users;
    return users.filter(u => selectedSite.assigned_users.includes(u.id));
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center pt-8 lg:pt-12 lg:items-center overflow-y-auto"
    >
      <div 
        className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Purple Gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Create Task</h2>
              <p className="text-purple-200 text-sm">Add a new task for your team</p>
            </div>
            <button 
              onClick={handleClose}
              disabled={isSubmitting || uploadingFiles}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Task Title */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                Task Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Install electrical panel - Floor 2"
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value.slice(0, 50)})}
                maxLength={50}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                required
              />
              <div className="text-right text-xs text-gray-500 mt-1">{newTask.title.length}/50</div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                Description
              </label>
              <textarea
                placeholder="Detailed description of the task..."
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                rows={3}
              />
            </div>

            {/* Site Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                Site <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={newTask.site_id}
                  onChange={(e) => setNewTask({...newTask, site_id: e.target.value, assigned_to: []})}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                  required
                >
                  <option value="" className="bg-slate-800">Select a site</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id} className="bg-slate-800">
                      {site.site_name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Priority - Pill Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                Priority
              </label>
              <div className="flex gap-3">
                {['low', 'medium', 'high', 'urgent'].map((priority) => (
                  <button
                    key={priority}
                    type="button"
                    onClick={() => setNewTask({...newTask, priority})}
                    className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                      newTask.priority === priority
                        ? priority === 'low' ? 'bg-green-500 text-white'
                          : priority === 'medium' ? 'bg-orange-500 text-white'
                          : priority === 'high' ? 'bg-red-500 text-white'
                          : 'bg-purple-600 text-white'
                        : 'bg-slate-800 text-gray-300 border border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Start Date & Due Date - Same Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                  Start Date <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="date"
                  value={newTask.start_date}
                  max={newTask.due_date || undefined}
                  onChange={(e) => setNewTask({...newTask, start_date: e.target.value})}
                  className="w-full h-10 px-4 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all [color-scheme:dark]"
                  data-testid="task-start-date-input"
                />
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                  Due Date <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="date"
                  value={newTask.due_date}
                  min={newTask.start_date || undefined}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  className="w-full h-10 px-4 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all [color-scheme:dark]"
                  data-testid="task-due-date-input"
                />
              </div>
            </div>
            {newTask.start_date && newTask.due_date && newTask.start_date > newTask.due_date && (
              <p className="-mt-2 text-xs text-rose-400" data-testid="task-start-date-error">
                Start date must be on or before the due date.
              </p>
            )}

            {/* Estimated Workload (own row) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                Estimated Workload (Days)
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNewTask({...newTask, task_capacity: Math.max(0.25, (newTask.task_capacity || 1) - 0.25)})}
                  className="w-8 h-8 rounded-md bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm flex items-center justify-center transition-colors flex-shrink-0"
                  data-testid="task-capacity-decrement-btn"
                >
                  −
                </button>
                <div className="flex-1 h-10 bg-slate-800 border border-slate-600 rounded-lg flex items-center justify-center" data-testid="task-capacity-value">
                  <span className="text-base font-bold text-white">{newTask.task_capacity.toFixed(2)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setNewTask({...newTask, task_capacity: (newTask.task_capacity || 1) + 0.25})}
                  className="w-8 h-8 rounded-md bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm flex items-center justify-center transition-colors flex-shrink-0"
                  data-testid="task-capacity-increment-btn"
                >
                  +
                </button>
              </div>
            </div>

            {/* Assign Users */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                Assign Users <span className="text-gray-500">(Optional)</span>
              </label>
              {!newTask.site_id ? (
                <p className="text-sm text-gray-500 italic">Select a site first to see available team members</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {[...getSiteUsers()].sort((a, b) => a.full_name.localeCompare(b.full_name)).map((user) => {
                    const isSelected = newTask.assigned_to.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => toggleUserAssignment(user.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                          isSelected
                            ? 'bg-purple-600 text-white border-purple-500'
                            : 'bg-slate-800 text-gray-300 border-slate-600 hover:border-purple-500'
                        }`}
                      >
                        <span className="font-medium">{user.full_name}</span>
                        {user.role && (
                          <span className={`block text-xs ${isSelected ? 'text-purple-200' : 'text-gray-500'}`}>
                            {user.role}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {getSiteUsers().length === 0 && (
                    <p className="text-sm text-gray-500 italic">No team members assigned to this site</p>
                  )}
                </div>
              )}
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Attachments
                  <span className="text-gray-500 ml-auto">{selectedFiles.length}/5</span>
                </div>
              </label>
              <p className="text-xs text-gray-500 mb-3">PDF, Images, Text files • Max 10MB each</p>
              
              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mb-3 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-800 rounded-lg p-3 border border-slate-700">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <div className="text-sm text-white">{file.name}</div>
                          <div className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Attachment Button */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.txt,.csv,.json,.md,image/*,application/pdf,text/*"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-purple-500/50 rounded-lg text-purple-400 hover:bg-purple-500/10 hover:border-purple-500 transition-all cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Attachment
              </label>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting || uploadingFiles}
            className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || uploadingFiles || !newTask.title || !newTask.site_id}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting || uploadingFiles ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {uploadingFiles ? 'Uploading...' : 'Creating...'}
              </>
            ) : (
              'Create Task'
            )}
          </button>
        </div>
      </div>
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        title={successModal.title}
        message={successModal.message}
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

export default AddTaskModal;
